import React, { useEffect, useMemo, useCallback } from 'react';
import { MdBarChart } from 'react-icons/md';

import OptimizationPipelineSidebarTable from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebarTable';
import { SidebarExportButtons, SidebarHeaderSearch, SidebarHeaderCheckbox } from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebar';
import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { exportSidebarTable, buildExportFilename } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const POPUP_TYPE = 'UNIT_OPTIMIZER_DETAILS_CHART';
const DIFF_EPS = 1e-6;

const UNIT_OPTIMIZER_TABLE_CONFIG = {
    columns: [
        {
            key: 'name',
            label: 'Attribute',
            sortable: true,
            searchable: true,
            align: 'left',
            minWidth: 200,
            truncate: true,
            tooltip: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'uom',
            label: 'UOM',
            sortable: true,
            searchable: true,
            align: 'right',
            minWidth: 90,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'base',
            label: 'Base State',
            sortable: true,
            align: 'right',
            minWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'best',
            label: 'Best State',
            sortable: true,
            align: 'right',
            minWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'gain',
            label: 'Change',
            sortable: true,
            align: 'right',
            minWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'pct',
            label: 'Change %',
            sortable: true,
            align: 'right',
            minWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'history',
            label: '',
            sortable: false,
            align: 'center',
            width: 64,
            exportable: false
        }
    ],
    search: {
        enabled: true,
        showInput: false
    },
    sort: {
        key: 'name',
        direction: 'asc'
    },
    states: {
        loadingTitle: 'Loading optimizer details...',
        emptyTitle: 'No unit optimizer data available.',
        noResultsTitle: 'No matching optimizer parameters found.',
        errorTitle: 'Failed to load optimizer details',
        errorDescription: 'Please try again later.'
    }
};

function isNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

function fmtNumber(value) {
    if (value == null || Number.isNaN(value)) return '-';

    if (isNumber(value)) {
        const abs = Math.abs(value);

        if (abs >= 100) return value.toFixed(1);
        if (abs >= 1) return value.toFixed(2);

        return value.toFixed(3);
    }

    if (typeof value === 'boolean') return value ? 'true' : 'false';

    return String(value);
}

function fmtPercent(value) {
    if (!isNumber(value)) return '-';
    return value.toFixed(2) + '%';
}

function flatten(obj, prefix = '', out = {}) {
    if (!obj || typeof obj !== 'object') return out;

    Object.entries(obj).forEach(([key, value]) => {
        if (key === 'id') return;

        const nextKey = prefix ? prefix + '.' + key : key;

        if (value && typeof value === 'object' && !Array.isArray(value)) {
            flatten(value, nextKey, out);
        } else {
            out[nextKey] = value;
        }
    });

    return out;
}

function parseComponents(components) {
    if (Array.isArray(components)) return components;
    if (components && typeof components === 'object') return Object.values(components);
    if (!components || typeof components !== 'string') return [];

    try {
        const parsed = JSON.parse(components);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
}

function rootFromPath(path) {
    return String(path || '').split(/[.\[]/)[0];
}

function buildUomMap(cleanMeasurementData, mergedMeasurementData) {
    const uomMap = new Map();
    const fields = [
        ...(Array.isArray(mergedMeasurementData?.fields) ? mergedMeasurementData.fields : []),
        ...(Array.isArray(cleanMeasurementData?.fields) ? cleanMeasurementData.fields : [])
    ];

    fields.forEach((field) => {
        if (!field?.name) return;

        const name = String(field.name);
        uomMap.set(name, field.uom ?? null);

        parseComponents(field.components).forEach((component) => {
            if (!component?.name) return;
            uomMap.set(name + '.' + String(component.name), field.uom ?? null);
        });
    });

    return uomMap;
}

function getUomForPath(path, uomMap) {
    if (!path || !uomMap || typeof uomMap.has !== 'function') return null;
    if (uomMap.has(path)) return uomMap.get(path) ?? null;

    return uomMap.get(rootFromPath(path)) ?? null;
}

function truncateUom(uom, max = 5, head = 3) {
    const stringValue = uom == null ? '' : String(uom);

    if (!stringValue) return '-';
    if (stringValue.length <= max) return stringValue;

    return stringValue.slice(0, head) + '...';
}

function hasRealDifference(row) {
    const { base, best } = row;

    if (isNumber(base) && isNumber(best)) {
        return Math.abs(best - base) > DIFF_EPS;
    }

    if (base != null && best != null) {
        return String(base) !== String(best);
    }

    return base != null || best != null;
}

function getRowColor(gain) {
    if (!isNumber(gain)) return null;
    if (gain > DIFF_EPS) return 'green';
    if (gain < -DIFF_EPS) return 'red';
    return null;
}

function buildBaseRows(detail, uomMap) {
    const baseState = detail?.optimizer_base_state || detail?.base_state || detail?.merged_data || {};
    const bestState = detail?.optimizer_best_state || detail?.best_state || {};

    const flatBase = flatten(baseState);
    const flatBest = flatten(bestState);

    const keys = Array.from(
        new Set([...Object.keys(flatBase), ...Object.keys(flatBest)])
    ).sort((a, b) => a.localeCompare(b, 'hu'));

    return keys.map((name) => {
        const base = flatBase[name];
        const best = flatBest[name];

        const gain = isNumber(base) && isNumber(best)
            ? best - base
            : undefined;

        const pct = isNumber(base) && base !== 0 && isNumber(best)
            ? (gain / Math.abs(base)) * 100
            : undefined;

        return {
            name,
            uom: getUomForPath(name, uomMap),
            base,
            best,
            gain,
            pct,
            hasDifference: hasRealDifference({ base, best })
        };
    });
}

function buildTableConfig(hasUomColumn) {
    return {
        ...UNIT_OPTIMIZER_TABLE_CONFIG,
        columns: UNIT_OPTIMIZER_TABLE_CONFIG.columns.filter((column) => {
            return hasUomColumn || column.key !== 'uom';
        })
    };
}

function buildTableRows(rows, onOpenTimeseries) {
    return rows.map((row) => ({
        id: row.name,
        rowColor: getRowColor(row.gain),
        searchableText: row.name + ' ' + (row.uom || ''),
        cells: {
            name: {
                value: row.name
            },
            uom: {
                value: row.uom || '-',
                displayValue: truncateUom(row.uom),
                exportValue: row.uom || '-',
                tooltip: row.uom && String(row.uom).length > 5 ? String(row.uom) : undefined
            },
            base: {
                value: row.base,
                displayValue: fmtNumber(row.base)
            },
            best: {
                value: row.best,
                displayValue: fmtNumber(row.best)
            },
            gain: {
                value: row.gain,
                displayValue: fmtNumber(row.gain)
            },
            pct: {
                value: row.pct,
                displayValue: fmtPercent(row.pct)
            },
            history: {
                component: typeof onOpenTimeseries === 'function' ? (
                    <button
                        type='button'
                        className='w-28 h-28 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200'
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onOpenTimeseries([row.name]);
                        }}
                    >
                        <MdBarChart className='text-16 text-gray-600' />
                    </button>
                ) : null
            }
        }
    }));
}

export function UnitOptimizerSidebarHeaderActions() {
    const {
        sidebar,
        openSidebar,
        optimizationPipelineDetail,
        ts,
        openPopup,
        getOnDemandResource,
        ensureOnDemandResource
    } = useOptimizationPipeline();

    const query = sidebar?.query || '';
    const diffOnly = !!sidebar?.diffOnly;
    const cleanMeasurementData = getOnDemandResource('cleanMeasurementData')?.data;
    const mergedMeasurementData = getOnDemandResource('mergedMeasurementData')?.data;

    useEffect(() => {
        ensureOnDemandResource('cleanMeasurementData');
        ensureOnDemandResource('mergedMeasurementData');
    }, [ensureOnDemandResource]);

    const uomMap = useMemo(() => {
        return buildUomMap(cleanMeasurementData, mergedMeasurementData);
    }, [cleanMeasurementData, mergedMeasurementData]);

    const allRows = useMemo(() => {
        return buildBaseRows(optimizationPipelineDetail, uomMap);
    }, [optimizationPipelineDetail, uomMap]);

    const filteredRows = useMemo(() => {
        if (!diffOnly) return allRows;
        return allRows.filter((row) => row.hasDifference);
    }, [allRows, diffOnly]);

    const exportRows = useMemo(() => {
        return buildTableRows(filteredRows);
    }, [filteredRows]);

    const tableConfig = useMemo(() => {
        return buildTableConfig(allRows.some((row) => row.uom));
    }, [allRows]);

    const defaultDiffKeys = useMemo(() => {
        const keys = allRows
            .filter((row) => row.hasDifference)
            .map((row) => row.name)
            .filter(Boolean);

        const MAX_DEFAULT = 25;

        return keys.length > MAX_DEFAULT ? keys.slice(0, MAX_DEFAULT) : keys;
    }, [allRows]);

    const handleExport = (format) => {
        exportSidebarTable({
            tableConfig,
            rows: exportRows,
            query,
            sort: sidebar?.sort,
            filename: buildExportFilename('unit-optimizer', ts),
            format
        });
    };

    const handleOpenTimeseries = () => {
        openPopup({
            type: POPUP_TYPE,
            requestToken: Date.now(),
            availableKeys: allRows.map((row) => row.name),
            defaultSelectedKeys: defaultDiffKeys,
            initialMode: 'diff'
        });
    };

    return (
        <div className='flex items-center gap-8'>
            <SidebarExportButtons
                onExcel={() => handleExport('excel')}
                onCsv={() => handleExport('csv')}
                disabled={!exportRows.length}
            />

            <button
                type='button'
                className='w-28 h-28 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200'
                title='Open parameter timeseries'
                onClick={handleOpenTimeseries}
            >
                <MdBarChart className='text-16 text-gray-700' />
            </button>

            <SidebarHeaderSearch
                value={query}
                onChange={(nextQuery) => { openSidebar({ ...sidebar, query: nextQuery }); }}
            />

            <SidebarHeaderCheckbox
                id='unit-optimizer-diff-only'
                label='Show only differences'
                checked={diffOnly}
                onChange={(checked) => { openSidebar({ ...sidebar, diffOnly: checked }); }}
            />

        </div>
    );
}

export default function UnitOptimizerSidebar() {
    const {
        sidebar,
        openSidebar,
        optimizationPipelineDetail,
        openPopup,
        getOnDemandResource,
        ensureOnDemandResource
    } = useOptimizationPipeline();

    const diffOnly = !!sidebar?.diffOnly;
    const cleanMeasurementData = getOnDemandResource('cleanMeasurementData')?.data;
    const mergedMeasurementData = getOnDemandResource('mergedMeasurementData')?.data;

    useEffect(() => {
        ensureOnDemandResource('cleanMeasurementData');
        ensureOnDemandResource('mergedMeasurementData');
    }, [ensureOnDemandResource]);

    const uomMap = useMemo(() => {
        return buildUomMap(cleanMeasurementData, mergedMeasurementData);
    }, [cleanMeasurementData, mergedMeasurementData]);

    const allRows = useMemo(() => {
        return buildBaseRows(optimizationPipelineDetail, uomMap);
    }, [optimizationPipelineDetail, uomMap]);

    const filteredRows = useMemo(() => {
        if (!diffOnly) return allRows;
        return allRows.filter((row) => row.hasDifference);
    }, [allRows, diffOnly]);

    const openTimeseries = useCallback((seedKeys) => {
        openPopup({
            type: POPUP_TYPE,
            requestToken: Date.now(),
            availableKeys: allRows.map((row) => row.name),
            defaultSelectedKeys: Array.isArray(seedKeys) ? seedKeys : [],
            initialMode: 'diff'
        });
    }, [allRows, openPopup]);

    const tableRows = useMemo(() => {
        return buildTableRows(filteredRows, openTimeseries);
    }, [filteredRows, openTimeseries]);

    const tableConfig = useMemo(() => {
        return buildTableConfig(allRows.some((row) => row.uom));
    }, [allRows]);

    const loading = !optimizationPipelineDetail?.merged_data;

    return (
        <div className='w-full h-full min-h-0 flex flex-col overflow-hidden'>
            <div className='w-full flex-1 min-h-0 overflow-hidden'>
                <OptimizationPipelineSidebarTable
                    tableConfig={tableConfig}
                    rows={tableRows}
                    loading={loading}
                    query={sidebar?.query || ''}
                    sort={sidebar?.sort}
                    onQueryChange={(nextQuery) => {
                        openSidebar({ ...sidebar, query: nextQuery });
                    }}
                    onSortChange={(nextSort) => {
                        openSidebar({ ...sidebar, sort: nextSort });
                    }}
                />
            </div>
        </div>
    );
}
